import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Send, PenLine, Copy, Eye, CheckCircle2, ShieldCheck, LayoutTemplate, Pencil, BookOpen, X } from "lucide-react";
import { api, API, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Btn, Badge, eur, dt } from "../components/common";
import { Modal, Field, TextArea, Selecter } from "./crm/crmui";
import ProposalDrawer from "../components/ProposalDrawer";
import { useAutoRefresh } from "../lib/autoRefresh";
import { ProposalPreview } from "../components/ProposalPreview";

const EMPTY_ITEM = { descricao: "", quantidade: 1, preco_unitario: 0, desconto_pct: 0, iva_pct: 23, periodo: "anual", plano_id: "" };
const PERIODO_LABEL = { anual: "Anual", "3anos": "3 anos", "5anos": "5 anos" };
const PERIODO_MONTHS = { anual: 12, "3anos": 36, "5anos": 60 };

/** Preco total do item para o periodo escolhido */
function getPlanPrice(item, periodo) {
  if (item._source !== "plan") return item.preco_base || 0;
  if (periodo === "3anos") return item.preco_3anos || item.preco_base || 0;
  if (periodo === "5anos") return item.preco_5anos || item.preco_base || 0;
  return item.preco_anual || item.preco_base || 0;
}

/** Converte um plano do catalogo para item do picker */
function planToItem(p) {
  return {
    id: p.id,
    nome: p.nome,
    sku: p.sku || "",
    descricao: [p.limites && `Limites: ${p.limites}`, p.max_utilizadores > 0 && `Max. ${p.max_utilizadores} utilizadores`].filter(Boolean).join(" | "),
    categoria: "Subscricao",
    plataforma: p._platform_nome || p.nome,
    iva_pct: p.taxa_iva_regiao || 23,
    plano_id: p.id,
    preco_base: p.preco_anual || 0,
    preco_anual: p.preco_anual || 0,
    preco_3anos: p.preco_3anos || 0,
    preco_5anos: p.preco_5anos || 0,
    preco_mensal_1ano: p.preco_mensal_1ano || 0,
    preco_mensal_3anos: p.preco_mensal_3anos || 0,
    preco_mensal_5anos: p.preco_mensal_5anos || 0,
    desconto_3anos: p.desconto_3anos || 0,
    desconto_5anos: p.desconto_5anos || 0,
    periodos_disponiveis: p.periodos_disponiveis || ["anual"],
    _source: "plan",
  };
}

export default function Proposals() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [catalogPlans, setCatalogPlans] = useState([]);
  const [catalogPeriodo, setCatalogPeriodo] = useState("anual");
  const [platforms, setPlatforms] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [showEdit, setShowEdit] = useState(null); // proposta a editar
  const [showSign, setShowSign] = useState(null);
  const [sigStatus, setSigStatus] = useState(null);
  const [prop, setProp] = useState({ titulo: "", account_id: "", notas: "", deal_id: "", itens: [{ ...EMPTY_ITEM }], desconto_pct_global: 0, condicoes_pagamento: "pronto", metodo_pagamento: "sepa", validade_dias: 30 });
  const [editProp, setEditProp] = useState(null); // estado do modal de edicao
  const [signer, setSigner] = useState({ signatario_nome: "", signatario_email: "", signatario_telefone: "", email: true, sms: false });
  const [drawer, setDrawer] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplAccountId, setTplAccountId] = useState("");
  // Template modal steps: "list" | "configure" | "edit"
  const [tplStep, setTplStep] = useState("list");
  const [selectedTpl, setSelectedTpl] = useState(null);
  const [tplItens, setTplItens] = useState([{ ...EMPTY_ITEM }]);
  const [editTplData, setEditTplData] = useState(null);
  const [showCatalog, setShowCatalog] = useState(null); // { target: "new"|"edit", idx: number }
  const [catalogSearch, setCatalogSearch] = useState("");

  const load = async () => {
    try {
      const [p, a, t, pr, pl, plat] = await Promise.all([
        api.get("/cpq/proposals"),
        api.get("/crm/accounts"),
        api.get("/cpq/proposal-templates"),
        api.get("/cpq/products").catch(() => ({ data: [] })),
        api.get("/plans").catch(() => ({ data: [] })),
        api.get("/platforms").catch(() => ({ data: [] })),
      ]);
      setRows(p.data); setAccounts(a.data); setTemplates(t.data || []); setProducts(pr.data || []);
      const platMap = Object.fromEntries((plat.data || []).map((x) => [x.id, x.nome]));
      setPlatforms(platMap);
      setCatalogPlans((pl.data || [])
        .filter((x) => x.ativo && !x.sku?.startsWith("test-iter") && !x.nome?.startsWith("TEST_"))
        .map((x) => ({ ...x, _platform_nome: platMap[x.plataforma_id] || x.nome })));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load); // eslint-disable-line

  // Abertura automatica com pre-preenchimento a partir de /app/crm/oportunidades
  useEffect(() => {
    const account_id = params.get("account_id") || "";
    const deal_id = params.get("deal_id") || "";
    const titulo = params.get("titulo") || "";
    const plataforma = params.get("plataforma") || "";
    const mrr = params.get("mrr");
    if (account_id || deal_id || titulo) {
      const acc = accounts.find((x) => x.id === account_id);
      const label = acc ? ` para ${acc.nome}` : "";
      setProp({
        titulo: titulo ? `Proposta: ${titulo}${label}` : (label ? `Proposta${label}` : ""),
        account_id,
        deal_id,
        notas: plataforma ? `Plataforma: ${plataforma}` : "",
        itens: [{ ...EMPTY_ITEM, descricao: plataforma || titulo || "", preco_unitario: mrr ? parseFloat(mrr) : 0 }],
      });
      setShowNew(true);
      const next = new URLSearchParams(params);
      ["account_id", "deal_id", "titulo", "plataforma", "mrr"].forEach((k) => next.delete(k));
      setParams(next, { replace: true });
    }
  }, [accounts]); // eslint-disable-line

  const accName = (id) => accounts.find((a) => a.id === id)?.nome || "-";

  // --- Helpers para linhas ---
  const setItem = (i, k, v) => setProp((s) => ({ ...s, itens: s.itens.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addItem = () => setProp((s) => ({ ...s, itens: [...s.itens, { ...EMPTY_ITEM }] }));
  const rmItem = (i) => setProp((s) => ({ ...s, itens: s.itens.filter((_, idx) => idx !== i) }));

  const setEditItem = (i, k, v) => setEditProp((s) => ({ ...s, itens: s.itens.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addEditItem = () => setEditProp((s) => ({ ...s, itens: [...s.itens, { ...EMPTY_ITEM }] }));
  const rmEditItem = (i) => setEditProp((s) => ({ ...s, itens: s.itens.filter((_, idx) => idx !== i) }));

  const computePreview = (itens) => itens.reduce((acc, it) => {
    const base = (parseFloat(it.quantidade) || 0) * (parseFloat(it.preco_unitario) || 0) * (1 - (parseFloat(it.desconto_pct) || 0) / 100);
    return { sub: acc.sub + base, iva: acc.iva + base * (parseFloat(it.iva_pct) || 0) / 100 };
  }, { sub: 0, iva: 0 });

  const preview = computePreview(prop.itens);
  const editPreview = editProp ? computePreview(editProp.itens) : { sub: 0, iva: 0 };

  // --- Catalog picker ---
  const allCatalogItems = [
    ...products.map((p) => ({ ...p, _source: "product" })),
    ...catalogPlans.map(planToItem),
  ];
  const filteredCatalogItems = allCatalogItems.filter((p) =>
    !catalogSearch ||
    p.nome?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
    p.sku?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
    p.plataforma?.toLowerCase().includes(catalogSearch.toLowerCase())
  );

  // Group filtered items by platform for the picker
  const groupedCatalogItems = useMemo(() => {
    const groups = {};
    filteredCatalogItems.forEach((item) => {
      const grp = item._source === "product" ? "Artigos" : (item.plataforma || "Outros");
      if (!groups[grp]) groups[grp] = [];
      groups[grp].push(item);
    });
    return Object.entries(groups).sort(([a, ai], [b, bi]) => {
      // "Artigos" always first
      if (a === "Artigos") return -1;
      if (b === "Artigos") return 1;
      return a.localeCompare(b);
    });
  }, [filteredCatalogItems]); // eslint-disable-line
  const selectFromCatalog = (item) => {
    const { target, idx } = showCatalog;
    const preco = getPlanPrice(item, catalogPeriodo);
    const lineItem = {
      descricao: item.nome || "",
      preco_unitario: preco,
      iva_pct: item.iva_pct || 23,
      desconto_pct: 0,
      quantidade: 1,
      periodo: item._source === "plan" ? catalogPeriodo : (item.periodo || "anual"),
      plano_id: item.plano_id || (item._source === "plan" ? item.id : ""),
      product_id: item._source === "product" ? item.id : "",
      preco_mensal_ref: item._source === "plan" ? (item.preco_mensal_1ano || 0) : 0,
    };
    if (target === "new") {
      setProp((s) => ({ ...s, itens: s.itens.map((it, i) => i === idx ? lineItem : it) }));
    } else if (target === "edit") {
      setEditProp((s) => ({ ...s, itens: s.itens.map((it, i) => i === idx ? lineItem : it) }));
    } else if (target === "configure-tpl") {
      setTplItens((s) => s.map((it, i) => i === idx ? lineItem : it));
    } else if (target === "edit-tpl") {
      setEditTplData((s) => ({ ...s, itens: (s.itens || []).map((it, i) => i === idx ? lineItem : it) }));
    }
    setShowCatalog(null); setCatalogSearch("");
  };

  const openCatalog = (target, idx, periodo) => {
    setCatalogPeriodo(periodo || "anual");
    setShowCatalog({ target, idx });
  };

  // --- Template modal helpers ---
  const closeTemplateModal = () => {
    setShowTemplates(false); setTplStep("list"); setSelectedTpl(null);
    setTplItens([{ ...EMPTY_ITEM }]); setEditTplData(null); setTplAccountId("");
  };
  const applyTpl = (t) => {
    setSelectedTpl(t);
    setTplItens((t.itens?.length ? t.itens : [EMPTY_ITEM]).map((it) => ({ ...EMPTY_ITEM, ...it })));
    setTplStep("configure");
  };
  const editTplOpen = (t) => { setEditTplData({ ...t, itens: (t.itens || []).map((i) => ({ ...i })) }); setTplStep("edit"); };
  const newTplOpen = () => {
    setEditTplData({ nome: "", descricao: "", plataforma: "", itens: [{ ...EMPTY_ITEM }], notas: "", validade_dias: 30, condicoes_pagamento: "pronto", metodo_pagamento: "sepa", ativo: true, desconto_pct_global: 0 });
    setTplStep("edit");
  };
  const setTplItem = (arr, setter, i, key, val) => setter((s) => ({ ...s, itens: (s.itens || []).map((it, j) => j === i ? { ...it, [key]: val } : it) }));
  const addTplItem = (setter) => setter((s) => ({ ...s, itens: [...(s.itens || []), { ...EMPTY_ITEM }] }));
  const removeTplItem = (setter, i) => setter((s) => ({ ...s, itens: (s.itens || []).filter((_, j) => j !== i) }));

  const createFromTemplateFn = async () => {
    if (!selectedTpl) return;
    try {
      const { data } = await api.post(`/cpq/proposals/from-template/${selectedTpl.id}`, {
        account_id: tplAccountId || "",
        itens_override: tplItens.map((it) => ({
          ...it, quantidade: parseFloat(it.quantidade) || 1,
          preco_unitario: parseFloat(it.preco_unitario) || 0,
          desconto_pct: parseFloat(it.desconto_pct) || 0,
          iva_pct: parseFloat(it.iva_pct) || 23,
        })),
      });
      toast.success(`Proposta ${data.numero} criada.`);
      closeTemplateModal(); load(); setDrawer(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [savingTpl, setSavingTpl] = useState(false);
  const saveTpl = async () => {
    if (!editTplData?.nome?.trim()) { toast.error("Indique um nome."); return; }
    setSavingTpl(true);
    try {
      if (editTplData.id) {
        await api.put(`/cpq/proposal-templates/${editTplData.id}`, editTplData);
        toast.success("Template actualizado.");
      } else {
        await api.post("/cpq/proposal-templates", editTplData);
        toast.success("Template criado.");
      }
      await load(); setTplStep("list"); setEditTplData(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingTpl(false); }
  };
  const deleteTpl = async () => {
    if (!editTplData?.id || !window.confirm(`Eliminar "${editTplData.nome}"?`)) return;
    try {
      await api.delete(`/cpq/proposal-templates/${editTplData.id}`);
      toast.success("Template eliminado."); await load(); setTplStep("list"); setEditTplData(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const create = async () => {
    if (!prop.titulo.trim()) return toast.error("Indique o titulo.");
    try {
      await api.post("/cpq/proposals", {
        titulo: prop.titulo, account_id: prop.account_id, notas: prop.notas, deal_id: prop.deal_id || "",
        desconto_pct_global: parseFloat(prop.desconto_pct_global) || 0,
        condicoes_pagamento: prop.condicoes_pagamento || "pronto",
        metodo_pagamento: prop.metodo_pagamento || "sepa",
        validade_dias: parseInt(prop.validade_dias) || 30,
        itens: prop.itens.map((it) => ({ ...it, quantidade: parseFloat(it.quantidade) || 0, preco_unitario: parseFloat(it.preco_unitario) || 0, desconto_pct: parseFloat(it.desconto_pct) || 0, iva_pct: parseFloat(it.iva_pct) || 0, periodo: it.periodo || "anual", plano_id: it.plano_id || "" })),
      });
      toast.success("Proposta criada."); setShowNew(false);
      setProp({ titulo: "", account_id: "", notas: "", deal_id: "", itens: [{ ...EMPTY_ITEM }], desconto_pct_global: 0, condicoes_pagamento: "pronto", metodo_pagamento: "sepa", validade_dias: 30 });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEdit = (r) => {
    setEditProp({
      titulo: r.titulo || "",
      account_id: r.account_id || "",
      notas: r.notas || "",
      deal_id: r.deal_id || "",
      desconto_pct_global: r.desconto_pct_global || 0,
      condicoes_pagamento: r.condicoes_pagamento || "pronto",
      metodo_pagamento: r.metodo_pagamento || "sepa",
      validade_dias: r.validade_dias || 30,
      itens: (r.itens || []).map((it) => ({ ...EMPTY_ITEM, ...it })),
    });
    setShowEdit(r);
  };

  const saveEdit = async () => {
    if (!editProp.titulo.trim()) return toast.error("Indique o titulo.");
    try {
      await api.put(`/cpq/proposals/${showEdit.id}`, {
        titulo: editProp.titulo, account_id: editProp.account_id, notas: editProp.notas, deal_id: editProp.deal_id || "",
        desconto_pct_global: parseFloat(editProp.desconto_pct_global) || 0,
        condicoes_pagamento: editProp.condicoes_pagamento || "pronto",
        metodo_pagamento: editProp.metodo_pagamento || "sepa",
        validade_dias: parseInt(editProp.validade_dias) || 30,
        itens: editProp.itens.map((it) => ({ ...it, quantidade: parseFloat(it.quantidade) || 0, preco_unitario: parseFloat(it.preco_unitario) || 0, desconto_pct: parseFloat(it.desconto_pct) || 0, iva_pct: parseFloat(it.iva_pct) || 0, periodo: it.periodo || "anual", plano_id: it.plano_id || "" })),
      });
      toast.success("Proposta actualizada."); setShowEdit(null); setEditProp(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openPdf = async (id) => {
    try {
      const { data } = await api.get(`/cpq/proposals/${id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (e) { toast.error("Nao foi possivel abrir o PDF."); }
  };

  const del = async (id) => { try { await api.delete(`/cpq/proposals/${id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const openSign = async (p) => {
    setShowSign(p); setSigStatus(null);
    setSigner({ signatario_nome: "", signatario_email: "", signatario_telefone: "", email: true, sms: false });
    try { const { data } = await api.get(`/cpq/proposals/${p.id}/signature`); if (data.exists) setSigStatus(data); } catch (e) { /* noop */ }
  };

  const sendSign = async () => {
    if (!signer.signatario_nome.trim()) return toast.error("Indique o nome do signatario.");
    const canais = [signer.email && "email", signer.sms && "sms"].filter(Boolean);
    try {
      await api.post(`/cpq/proposals/${showSign.id}/enviar-assinatura`, {
        signatario_nome: signer.signatario_nome, signatario_email: signer.signatario_email,
        signatario_telefone: signer.signatario_telefone, canais,
      });
      toast.success("Enviado para assinatura.");
      const { data } = await api.get(`/cpq/proposals/${showSign.id}/signature`);
      setSigStatus(data); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409) toast.warning(formatApiErrorDetail(d) + " Aprove em Aprovacoes e volte a enviar.");
      else toast.error(formatApiErrorDetail(d));
      load();
    }
  };

  const copyLink = (token) => { navigator.clipboard.writeText(`${window.location.origin}/assinar/${token}`); toast.success("Link copiado."); };

  const aceitar = async (r) => {
    if (!window.confirm(`Aceitar proposta ${r.numero}? Isto pode gerar encomenda, contrato, fatura e provisionar tenant (segundo o Workflow CPQ).`)) return;
    try {
      const { data } = await api.post(`/cpq/proposals/${r.id}/aceitar`, {});
      const created = [
        data.order_id && "encomenda",
        data.contract_id && "contrato",
        data.invoice_id && "fatura",
        data.subscription_id && "subscricao",
      ].filter(Boolean);
      toast.success(`Proposta aceite. ${created.length ? "Criados: " + created.join(", ") : ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const aprovar = async (r) => {
    try {
      await api.post(`/cpq/proposals/${r.id}/aprovar`);
      toast.success(`Proposta ${r.numero} aprovada.`); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createFromTemplate = async (tid) => {
    try {
      const { data } = await api.post(`/cpq/proposals/from-template/${tid}`, { account_id: tplAccountId || "" });
      toast.success(`Proposta ${data.numero} criada a partir do template.`);
      setShowTemplates(false); setTplAccountId(""); load();
      setDrawer(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cols = [
    { key: "numero", label: "Numero", render: (r) => (
      <button onClick={() => setDrawer(r)} data-testid={`proposal-drawer-open-${r.id}`}
        className="font-semibold text-navy hover:text-blue hover:underline text-left">{r.numero}</button>
    ) },
    { key: "titulo", label: "Titulo" },
    { key: "account_id", label: "Cliente", render: (r) => accName(r.account_id) },
    { key: "total", label: "Total", render: (r) => eur(r.total) },
    { key: "estado", label: "Estado", render: (r) => (
      <div className="flex items-center gap-1">
        <Badge value={r.estado} />
        {r.aprovacao_pendente && <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" data-testid={`aprov-pend-${r.id}`}>APROV. PENDENTE</span>}
        {r.desconto_pct_global > 0 && <span className="text-[9px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">-{r.desconto_pct_global}%</span>}
      </div>
    ) },
    { key: "criado_em", label: "Criado", render: (r) => dt(r.criado_em) },
    { key: "acoes", label: "", render: (r) => (
      <div className="flex gap-1">
        <button onClick={() => setDrawer(r)} data-testid={`proposal-drawer-btn-${r.id}`} title="Ver detalhes" className="text-slate-700 hover:bg-slate-100 rounded p-1"><Eye size={15} /></button>
        <button onClick={() => openPdf(r.id)} data-testid={`pdf-${r.id}`} title="Ver PDF" className="text-blue hover:bg-blue/10 rounded p-1"><FileText size={15} /></button>
        {!["aceite", "assinada", "cancelada", "expirada"].includes(r.estado) && (
          <button onClick={() => openEdit(r)} data-testid={`edit-prop-${r.id}`} title="Editar proposta" className="text-amber-600 hover:bg-amber-50 rounded p-1"><Pencil size={15} /></button>
        )}
        {r.aprovacao_pendente && (
          <button onClick={() => aprovar(r)} data-testid={`approve-${r.id}`} title="Aprovar desconto" className="text-amber-700 hover:bg-amber-50 rounded p-1"><ShieldCheck size={15} /></button>
        )}
        {!["aceite", "assinada", "cancelada", "expirada"].includes(r.estado) && !r.aprovacao_pendente && (
          <button onClick={() => aceitar(r)} data-testid={`accept-${r.id}`} title="Aceitar proposta" className="text-emerald-700 hover:bg-emerald-50 rounded p-1"><CheckCircle2 size={15} /></button>
        )}
        {r.estado !== "assinada" && <button onClick={() => openSign(r)} data-testid={`sign-${r.id}`} title="Assinatura" className="text-navy hover:bg-tint rounded p-1"><PenLine size={15} /></button>}
        {r.estado === "rascunho" && <button onClick={() => del(r.id)} data-testid={`del-prop-${r.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={15} /></button>}
      </div>
    ) },
  ];

  return (
    <>
      <PageHeader overline="CPQ" title="Propostas" action={
        <div className="flex items-center gap-2">
          <Btn variant="ghost" onClick={() => setShowTemplates(true)} testid="from-template-btn"><LayoutTemplate size={16} /> Criar de template</Btn>
          <Btn onClick={() => setShowNew(true)} testid="new-proposal-btn"><Plus size={16} /> Nova proposta</Btn>
        </div>
      } />
      <Table testid="proposals-table" columns={cols} rows={rows} />

      <Modal open={showTemplates} onClose={closeTemplateModal}
        title={tplStep === "list" ? "Templates de proposta" : tplStep === "configure" ? `Configurar: ${selectedTpl?.nome || ""}` : editTplData?.id ? "Editar template" : "Novo template"}
        testid="templates-modal">

        {/* ── STEP LIST ── */}
        {tplStep === "list" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500">{templates.filter((t) => t.ativo).length} activo(s) de {templates.length} total</div>
              <Btn onClick={newTplOpen} variant="ghost" testid="new-tpl-btn"><Plus size={12} /> Novo template</Btn>
            </div>
            <div className="space-y-1.5 max-h-[56vh] overflow-y-auto pr-1" data-testid="templates-list">
              {templates.length === 0 && <div className="text-sm text-slate-500 italic p-2">Sem templates. Crie um com o botao acima.</div>}
              {(() => {
                // Group by platform
                const groups = {};
                templates.forEach((t) => { const g = t.plataforma || "Geral"; if (!groups[g]) groups[g] = []; groups[g].push(t); });
                return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([grp, tpls]) => (
                  <div key={grp} className="mb-2">
                    <div className="text-[10px] font-bold text-navy uppercase tracking-widest px-1 pb-1 border-b border-slate-100 mb-1">{grp}</div>
                    {tpls.map((t) => (
                      <div key={t.id} data-testid={`tpl-row-${t.id}`}
                        className={`flex items-start justify-between gap-2 border rounded-lg px-3 py-2 mb-1 ${t.ativo ? "border-tint bg-white" : "border-dashed border-slate-200 bg-slate-50 opacity-60"}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-navy text-sm flex items-center gap-1.5">
                            {t.nome}
                            {!t.ativo && <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">INATIVO</span>}
                            {t.competitive_upgrade && <span className="text-[9px] font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">CU</span>}
                          </div>
                          {t.descricao && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{t.descricao}</div>}
                          <div className="text-[10px] text-slate-400 mt-0.5">{(t.itens || []).length} linha(s) - {t.validade_dias}d validade</div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Btn onClick={() => editTplOpen(t)} variant="ghost" testid={`tpl-edit-${t.id}`}><Pencil size={12} /></Btn>
                          {t.ativo && <Btn onClick={() => applyTpl(t)} testid={`tpl-use-${t.id}`}>Usar</Btn>}
                        </div>
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {/* ── STEP CONFIGURE ── */}
        {tplStep === "configure" && selectedTpl && (
          <div className="space-y-3">
            <button onClick={() => setTplStep("list")} className="text-xs text-blue hover:underline flex items-center gap-1"><X size={11} /> Voltar a lista</button>
            <Selecter label="Cliente (opcional)" value={tplAccountId} onChange={setTplAccountId}
              options={[{ value: "", label: "- Sem cliente -" }, ...accounts.map((a) => ({ value: a.id, label: a.nome }))]}
              testid="tpl-account" />
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-semibold text-navy">Linhas da proposta</label>
                <div className="flex gap-2">
                  <button onClick={() => openCatalog("configure-tpl", tplItens.length - 1, tplItens[tplItens.length - 1]?.periodo)}
                    className="text-blue text-xs hover:underline flex items-center gap-1"><BookOpen size={11} /> Catalogo</button>
                  <button onClick={() => setTplItens((s) => [...s, { ...EMPTY_ITEM }])} className="text-blue text-xs hover:underline">+ Linha</button>
                </div>
              </div>
              <div className="space-y-1.5 max-h-52 overflow-y-auto">
                {tplItens.map((it, i) => (
                  <div key={i} className="grid grid-cols-13 gap-1 items-center" data-testid={`configure-item-${i}`}>
                    <button onClick={() => openCatalog("configure-tpl", i, it.periodo)} title="Catalogo" className="text-blue/70 hover:text-blue rounded p-1 w-6 h-6 flex items-center justify-center"><BookOpen size={11} /></button>
                    <input className="col-span-4 px-2 py-1.5 border border-tint rounded text-xs" placeholder="Descricao" value={it.descricao}
                      onChange={(e) => setTplItens((s) => s.map((x, j) => j === i ? { ...x, descricao: e.target.value } : x))} />
                    <select className="col-span-2 px-1 py-1.5 border border-tint rounded text-xs" value={it.periodo || "anual"}
                      onChange={(e) => setTplItens((s) => s.map((x, j) => j === i ? { ...x, periodo: e.target.value } : x))}>
                      <option value="anual">1 ano</option>
                      <option value="3anos">3 anos</option>
                      <option value="5anos">5 anos</option>
                    </select>
                    <input className="col-span-1 px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="Qtd" value={it.quantidade}
                      onChange={(e) => setTplItens((s) => s.map((x, j) => j === i ? { ...x, quantidade: e.target.value } : x))} />
                    <input className="col-span-2 px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="Preco" value={it.preco_unitario}
                      onChange={(e) => setTplItens((s) => s.map((x, j) => j === i ? { ...x, preco_unitario: e.target.value } : x))} />
                    <input className="col-span-2 px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="IVA%" value={it.iva_pct}
                      onChange={(e) => setTplItens((s) => s.map((x, j) => j === i ? { ...x, iva_pct: e.target.value } : x))} />
                    <button onClick={() => setTplItens((s) => s.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 w-5 h-5 flex items-center justify-center"><X size={11} /></button>
                  </div>
                ))}
              </div>
            </div>
            <Btn onClick={createFromTemplateFn} testid="create-from-tpl-btn" className="w-full">Criar proposta</Btn>
          </div>
        )}

        {/* ── STEP EDIT ── */}
        {tplStep === "edit" && editTplData && (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <button onClick={() => { setTplStep("list"); setEditTplData(null); }} className="text-xs text-blue hover:underline flex items-center gap-1"><X size={11} /> Voltar a lista</button>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Nome" value={editTplData.nome} onChange={(v) => setEditTplData((s) => ({ ...s, nome: v }))} testid="tpl-nome" />
              <div>
                <label className="block text-[11px] font-semibold text-navy mb-1">Plataforma</label>
                <select className="w-full px-2 py-1.5 border border-tint rounded text-sm"
                  value={editTplData.plataforma || ""}
                  onChange={(e) => setEditTplData((s) => ({ ...s, plataforma: e.target.value }))}
                  data-testid="tpl-plataforma">
                  <option value="">- Geral -</option>
                  {Object.entries(platforms).map(([id, nome]) => <option key={id} value={nome.toLowerCase()}>{nome}</option>)}
                </select>
              </div>
            </div>
            <Field label="Descricao" value={editTplData.descricao || ""} onChange={(v) => setEditTplData((s) => ({ ...s, descricao: v }))} testid="tpl-descricao" />
            <div className="flex items-center gap-3 text-sm">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={editTplData.ativo !== false}
                  onChange={(e) => setEditTplData((s) => ({ ...s, ativo: e.target.checked }))} className="rounded" />
                <span className="text-navy font-medium">Template activo</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={editTplData.competitive_upgrade || false}
                  onChange={(e) => setEditTplData((s) => ({ ...s, competitive_upgrade: e.target.checked }))} className="rounded" />
                <span className="text-navy font-medium">Competitive Upgrade</span>
              </label>
            </div>
            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-semibold text-navy">Linhas do template</label>
                <div className="flex gap-2">
                  <button onClick={() => { const idx = (editTplData.itens?.length || 1) - 1; openCatalog("edit-tpl", idx, editTplData.itens?.[idx]?.periodo); }}
                    className="text-blue text-xs hover:underline flex items-center gap-1"><BookOpen size={11} /> Catalogo</button>
                  <button onClick={() => addTplItem(setEditTplData)} className="text-blue text-xs hover:underline">+ Linha</button>
                </div>
              </div>
              <div className="space-y-1.5">
                {(editTplData.itens || []).map((it, i) => (
                  <div key={i} className="grid grid-cols-13 gap-1 items-center" data-testid={`edit-tpl-item-${i}`}>
                    <button onClick={() => openCatalog("edit-tpl", i, it.periodo)} title="Catalogo" className="text-blue/70 hover:text-blue rounded p-1 w-6 h-6 flex items-center justify-center"><BookOpen size={11} /></button>
                    <input className="col-span-4 px-2 py-1.5 border border-tint rounded text-xs" placeholder="Descricao" value={it.descricao}
                      onChange={(e) => setTplItem(null, setEditTplData, i, "descricao", e.target.value)} />
                    <select className="col-span-2 px-1 py-1.5 border border-tint rounded text-xs" value={it.periodo || "anual"}
                      onChange={(e) => setTplItem(null, setEditTplData, i, "periodo", e.target.value)}>
                      <option value="anual">1 ano</option>
                      <option value="3anos">3 anos</option>
                      <option value="5anos">5 anos</option>
                    </select>
                    <input className="col-span-1 px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="Qtd" value={it.quantidade}
                      onChange={(e) => setTplItem(null, setEditTplData, i, "quantidade", e.target.value)} />
                    <input className="col-span-2 px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="Preco" value={it.preco_unitario}
                      onChange={(e) => setTplItem(null, setEditTplData, i, "preco_unitario", e.target.value)} />
                    <input className="col-span-2 px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="IVA%" value={it.iva_pct}
                      onChange={(e) => setTplItem(null, setEditTplData, i, "iva_pct", e.target.value)} />
                    <button onClick={() => removeTplItem(setEditTplData, i)} className="text-red-400 hover:text-red-600 w-5 h-5 flex items-center justify-center"><X size={11} /></button>
                  </div>
                ))}
              </div>
            </div>
            <Field label="Notas" value={editTplData.notas || ""} onChange={(v) => setEditTplData((s) => ({ ...s, notas: v }))} testid="tpl-notas" />
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-navy mb-1">Validade (dias)</label>
                <input type="number" className="w-full px-2 py-1.5 border border-tint rounded text-sm"
                  value={editTplData.validade_dias || 30}
                  onChange={(e) => setEditTplData((s) => ({ ...s, validade_dias: parseInt(e.target.value) || 30 }))}
                  data-testid="tpl-validade" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-navy mb-1">Condicoes pagamento</label>
                <select className="w-full px-2 py-1.5 border border-tint rounded text-sm"
                  value={editTplData.condicoes_pagamento || "pronto"}
                  onChange={(e) => setEditTplData((s) => ({ ...s, condicoes_pagamento: e.target.value }))}
                  data-testid="tpl-cond-pag">
                  <option value="pronto">Pronto pagamento</option>
                  <option value="30d">30 dias</option>
                  <option value="60d">60 dias</option>
                  <option value="mensal">Mensalidade</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-navy mb-1">Metodo pagamento</label>
                <select className="w-full px-2 py-1.5 border border-tint rounded text-sm"
                  value={editTplData.metodo_pagamento || "sepa"}
                  onChange={(e) => setEditTplData((s) => ({ ...s, metodo_pagamento: e.target.value }))}
                  data-testid="tpl-metodo-pag">
                  <option value="sepa">SEPA/Debito directo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="mbway">MBWay</option>
                  <option value="cartao">Cartao</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-tint">
              {editTplData.id ? (
                <button onClick={deleteTpl} className="text-red-600 text-xs hover:underline flex items-center gap-1"><Trash2 size={12} /> Eliminar</button>
              ) : <div />}
              <Btn onClick={saveTpl} disabled={!!savingTpl} testid="save-tpl-btn">{savingTpl ? "A guardar..." : "Guardar template"}</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={showNew} onClose={() => setShowNew(false)} title="Nova proposta" testid="proposal-modal" size="2xl">
        <div className="flex gap-6 min-h-0">
          {/* ── Form (left) ── */}
          <div className="flex-1 min-w-0 space-y-3 overflow-y-auto max-h-[75vh] pr-1">
        <Field label="Titulo" value={prop.titulo} onChange={(v) => setProp({ ...prop, titulo: v })} testid="prop-titulo" />
        <Selecter label="Cliente" value={prop.account_id} onChange={(v) => setProp({ ...prop, account_id: v })} options={[{ value: "", label: "- Selecionar conta -" }, ...accounts.map((a) => ({ value: a.id, label: a.nome }))]} testid="prop-account" />
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-semibold text-navy">Linhas</label>
            <div className="flex gap-2">
              <button onClick={() => { const idx = prop.itens.length - 1; openCatalog("new", idx, prop.itens[idx]?.periodo); }} data-testid="open-catalog-btn" className="text-blue text-sm hover:underline flex items-center gap-1"><BookOpen size={12} /> Catalogo</button>
              <button onClick={addItem} data-testid="add-item-btn" className="text-blue text-sm hover:underline">+ Linha</button>
            </div>
          </div>
          <div className="space-y-2">
            {prop.itens.map((it, i) => (
              <div key={i} className="grid grid-cols-13 gap-1 items-center" data-testid={`prop-item-row-${i}`}>
                <button onClick={() => openCatalog("new", i, it.periodo)} title="Escolher do catalogo" className="text-blue/70 hover:text-blue hover:bg-blue/10 rounded p-1 w-6 h-6 flex items-center justify-center flex-shrink-0"><BookOpen size={11} /></button>
                <input className="col-span-4 px-2 py-1.5 border border-tint rounded text-xs" placeholder="Descricao" value={it.descricao} onChange={(e) => setItem(i, "descricao", e.target.value)} data-testid={`item-desc-${i}`} />
                <select className="col-span-2 px-1 py-1.5 border border-tint rounded text-xs" value={it.periodo || "anual"} onChange={(e) => setItem(i, "periodo", e.target.value)} data-testid={`item-periodo-${i}`}>
                  <option value="anual">1 ano</option>
                  <option value="3anos">3 anos</option>
                  <option value="5anos">5 anos</option>
                </select>
                <input className="col-span-1 px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="Qtd" value={it.quantidade} onChange={(e) => setItem(i, "quantidade", e.target.value)} data-testid={`item-qtd-${i}`} />
                <input className="col-span-2 px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="Preco" value={it.preco_unitario} onChange={(e) => setItem(i, "preco_unitario", e.target.value)} data-testid={`item-preco-${i}`} />
                <input className="col-span-2 px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="IVA%" value={it.iva_pct} onChange={(e) => setItem(i, "iva_pct", e.target.value)} data-testid={`item-iva-${i}`} />
                <button className="text-red-500" onClick={() => rmItem(i)} data-testid={`item-rm-${i}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <label className="text-xs font-semibold text-navy">Desconto global (%)</label>
            <input type="number" min="0" max="100" value={prop.desconto_pct_global}
              onChange={(e) => setProp({ ...prop, desconto_pct_global: e.target.value })}
              data-testid="prop-desconto-global"
              className="w-full mt-1 px-2 py-1.5 border border-tint rounded text-sm" />
            <div className="text-[10px] text-slate-500 mt-1">Descontos acima do threshold em Definicoes -&gt; Workflow CPQ exigem aprovacao.</div>
          </div>
          <div>
            <label className="text-xs font-semibold text-navy">Validade (dias)</label>
            <input type="number" min="1" value={prop.validade_dias}
              onChange={(e) => setProp({ ...prop, validade_dias: e.target.value })}
              data-testid="prop-validade"
              className="w-full mt-1 px-2 py-1.5 border border-tint rounded text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-navy">Condicoes pagamento</label>
            <select value={prop.condicoes_pagamento} onChange={(e) => setProp({ ...prop, condicoes_pagamento: e.target.value })}
              data-testid="prop-condicoes" className="w-full mt-1 px-2 py-1.5 border border-tint rounded text-sm bg-white">
              <option value="pronto">Pronto pagamento</option>
              <option value="30d">30 dias</option>
              <option value="60d">60 dias</option>
              <option value="90d">90 dias</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-navy">Metodo pagamento</label>
            <select value={prop.metodo_pagamento} onChange={(e) => setProp({ ...prop, metodo_pagamento: e.target.value })}
              data-testid="prop-metodo" className="w-full mt-1 px-2 py-1.5 border border-tint rounded text-sm bg-white">
              <option value="sepa">SEPA</option>
              <option value="mb">Multibanco</option>
              <option value="cartao">Cartao</option>
              <option value="transferencia">Transferencia</option>
            </select>
          </div>
        </div>
        <TextArea label="Notas" value={prop.notas} onChange={(v) => setProp({ ...prop, notas: v })} testid="prop-notas" />
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Btn><Btn onClick={create} testid="prop-save">Criar proposta</Btn></div>
          </div>
          {/* ── Preview (right) ── */}
          <div className="w-80 flex-shrink-0 overflow-y-auto max-h-[75vh]" data-testid="proposal-preview-panel">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block animate-pulse" />
              Preview em directo
            </div>
            <ProposalPreview prop={prop} accounts={accounts} />
          </div>
        </div>
      </Modal>

      {/* Modal de Edicao */}
      <Modal open={!!showEdit && !!editProp} onClose={() => { setShowEdit(null); setEditProp(null); }} title={`Editar proposta ${showEdit?.numero || ""}`} testid="edit-proposal-modal" size="2xl">
        {editProp && (
          <div className="flex gap-6 min-h-0">
            {/* ── Form (left) ── */}
            <div className="flex-1 min-w-0 space-y-3 overflow-y-auto max-h-[75vh] pr-1">
            <Field label="Titulo" value={editProp.titulo} onChange={(v) => setEditProp({ ...editProp, titulo: v })} testid="edit-prop-titulo" />
            <Selecter label="Cliente" value={editProp.account_id} onChange={(v) => setEditProp({ ...editProp, account_id: v })} options={[{ value: "", label: "- Selecionar conta -" }, ...accounts.map((a) => ({ value: a.id, label: a.nome }))]} testid="edit-prop-account" />
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-semibold text-navy">Linhas</label>
                <div className="flex gap-2">
                  <button onClick={() => { const idx = editProp.itens.length - 1; openCatalog("edit", idx, editProp.itens[idx]?.periodo); }} className="text-blue text-sm hover:underline flex items-center gap-1"><BookOpen size={12} /> Catalogo</button>
                  <button onClick={addEditItem} data-testid="add-edit-item-btn" className="text-blue text-sm hover:underline">+ Linha</button>
                </div>
              </div>
              <div className="space-y-2">
                {editProp.itens.map((it, i) => (
                  <div key={i} className="flex gap-1 items-center" data-testid={`edit-item-row-${i}`}>
                    <button onClick={() => openCatalog("edit", i, it.periodo)} title="Escolher do catalogo" className="text-blue/70 hover:text-blue hover:bg-blue/10 rounded p-1 w-6 h-6 flex items-center justify-center flex-shrink-0"><BookOpen size={11} /></button>
                    <input className="flex-[4] px-2 py-1.5 border border-tint rounded text-xs min-w-0" placeholder="Descricao" value={it.descricao} onChange={(e) => setEditItem(i, "descricao", e.target.value)} data-testid={`edit-item-desc-${i}`} />
                    <select className="flex-[2] px-1 py-1.5 border border-tint rounded text-xs" value={it.periodo || "anual"} onChange={(e) => setEditItem(i, "periodo", e.target.value)}>
                      <option value="anual">1 ano</option>
                      <option value="3anos">3 anos</option>
                      <option value="5anos">5 anos</option>
                    </select>
                    <input className="flex-[1] px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="Qtd" value={it.quantidade} onChange={(e) => setEditItem(i, "quantidade", e.target.value)} />
                    <input className="flex-[2] px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="Preco" value={it.preco_unitario} onChange={(e) => setEditItem(i, "preco_unitario", e.target.value)} />
                    <input className="flex-[1] px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="IVA%" value={it.iva_pct} onChange={(e) => setEditItem(i, "iva_pct", e.target.value)} />
                    <button className="text-red-500" onClick={() => rmEditItem(i)}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <label className="text-xs font-semibold text-navy">Desconto global (%)</label>
                <input type="number" min="0" max="100" value={editProp.desconto_pct_global} onChange={(e) => setEditProp({ ...editProp, desconto_pct_global: e.target.value })} className="w-full mt-1 px-2 py-1.5 border border-tint rounded text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-navy">Validade (dias)</label>
                <input type="number" min="1" value={editProp.validade_dias} onChange={(e) => setEditProp({ ...editProp, validade_dias: e.target.value })} className="w-full mt-1 px-2 py-1.5 border border-tint rounded text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-navy">Condicoes pagamento</label>
                <select value={editProp.condicoes_pagamento} onChange={(e) => setEditProp({ ...editProp, condicoes_pagamento: e.target.value })} className="w-full mt-1 px-2 py-1.5 border border-tint rounded text-sm bg-white">
                  <option value="pronto">Pronto pagamento</option><option value="30d">30 dias</option><option value="60d">60 dias</option><option value="90d">90 dias</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-navy">Metodo pagamento</label>
                <select value={editProp.metodo_pagamento} onChange={(e) => setEditProp({ ...editProp, metodo_pagamento: e.target.value })} className="w-full mt-1 px-2 py-1.5 border border-tint rounded text-sm bg-white">
                  <option value="sepa">SEPA</option><option value="mb">Multibanco</option><option value="cartao">Cartao</option><option value="transferencia">Transferencia</option>
                </select>
              </div>
            </div>
            <TextArea label="Notas" value={editProp.notas} onChange={(v) => setEditProp({ ...editProp, notas: v })} />
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => { setShowEdit(null); setEditProp(null); }}>Cancelar</Btn>
              <Btn onClick={saveEdit} testid="edit-prop-save">Guardar alteracoes</Btn>
            </div>
            </div>
            {/* ── Preview (right) ── */}
            <div className="w-80 flex-shrink-0 overflow-y-auto max-h-[75vh]" data-testid="edit-proposal-preview-panel">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block animate-pulse" />
                Preview em directo
              </div>
              <ProposalPreview prop={editProp} accounts={accounts} numero={showEdit?.numero} />
            </div>
          </div>
        )}
      </Modal>

      {/* Catalog Picker Modal */}
      {showCatalog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" data-testid="catalog-picker-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2"><BookOpen size={16} className="text-blue" /><span className="font-semibold text-navy">Escolher produto do catalogo</span></div>
              <button onClick={() => { setShowCatalog(null); setCatalogSearch(""); }} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            {/* Period selector + search */}
            <div className="px-4 py-3 border-b border-slate-100 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-navy">Periodo de subscricao:</span>
                {["anual", "3anos", "5anos"].map((p) => (
                  <button key={p} onClick={() => setCatalogPeriodo(p)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${catalogPeriodo === p ? "bg-blue text-white border-blue" : "border-tint text-navy hover:border-blue hover:text-blue"}`}
                    data-testid={`catalog-period-${p}`}>
                    {PERIODO_LABEL[p]}
                  </button>
                ))}
              </div>
              <input autoFocus value={catalogSearch} onChange={(e) => setCatalogSearch(e.target.value)}
                placeholder="Pesquisar por nome, plataforma ou SKU..." data-testid="catalog-search"
                className="w-full px-3 py-2 border border-tint rounded-lg text-sm focus:outline-none focus:border-blue" />
            </div>
            {/* List - grouped by platform */}
            <div className="flex-1 overflow-y-auto p-2">
              {groupedCatalogItems.length === 0 && <div className="text-center text-slate-400 py-8 text-sm">Nenhum produto encontrado</div>}
              {groupedCatalogItems.map(([grpNome, grpItems]) => (
                <div key={grpNome} className="mb-2">
                  <div className="sticky top-0 bg-white/95 backdrop-blur-sm px-2 py-1 flex items-center gap-2 border-b border-slate-100 mb-1 z-10">
                    <span className="text-[10px] font-bold text-navy uppercase tracking-widest">{grpNome}</span>
                    <span className="text-[10px] text-slate-400">{grpItems.length} plano{grpItems.length !== 1 ? "s" : ""}</span>
                  </div>
                  {grpItems.map((item) => {
                    const preco = getPlanPrice(item, catalogPeriodo);
                    const mensal = item._source === "plan"
                      ? (catalogPeriodo === "3anos" ? item.preco_mensal_3anos : catalogPeriodo === "5anos" ? item.preco_mensal_5anos : item.preco_mensal_1ano)
                      : null;
                    const desc = item._source === "plan"
                      ? (catalogPeriodo === "3anos" ? item.desconto_3anos : catalogPeriodo === "5anos" ? item.desconto_5anos : 0)
                      : 0;
                    return (
                      <button key={`${item._source}-${item.id}`} onClick={() => selectFromCatalog(item)}
                        data-testid={`catalog-item-${item.id}`}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue/5 hover:border-blue/20 border border-transparent transition mb-0.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-medium text-navy text-sm">{item.nome}</span>
                              {item.sku && <span className="text-[10px] text-slate-400 font-mono">{item.sku}</span>}
                              {item._source === "plan" && (catalogPeriodo === "3anos" || catalogPeriodo === "5anos") && desc > 0 && (
                                <span className="text-[9px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">-{desc}%</span>
                              )}
                            </div>
                            {item.descricao && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{item.descricao}</div>}
                          </div>
                          <div className="text-right flex-shrink-0 min-w-[72px]">
                            <div className="font-bold text-blue text-sm">{eur(preco)}</div>
                            {mensal != null && mensal > 0 && (
                              <div className="text-[10px] text-slate-400">{eur(mensal)}/mes</div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-400">
              {filteredCatalogItems.length} item{filteredCatalogItems.length !== 1 ? "s" : ""} | Seleccione para preencher a linha com o preco de {PERIODO_LABEL[catalogPeriodo]}
            </div>
          </div>
        </div>
      )}

      <Modal open={!!showSign} onClose={() => setShowSign(null)} title={`Assinatura - ${showSign?.numero || ""}`} testid="signature-modal">
        {sigStatus?.exists ? (
          <div className="space-y-3" data-testid="signature-status">
            <div className="flex items-center gap-2"><span className="text-sm font-semibold text-navy">Estado:</span> <Badge value={sigStatus.estado} /></div>
            <div className="text-sm">Signatario: {sigStatus.signatario_nome} ({sigStatus.signatario_email})</div>
            <div className="text-sm">Entrega OTP: {sigStatus.entregue ? "enviado" : "nao entregue"} - canais: {(sigStatus.canais || []).join(", ")}</div>
            {sigStatus.otp_fallback && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm" data-testid="otp-fallback">
                Nao foi possivel entregar o OTP (verifique o remetente SendGrid). Codigo para transmitir ao signatario: <b className="text-lg">{sigStatus.otp_fallback}</b>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input readOnly value={`${window.location.origin}/assinar/${sigStatus.token}`} className="flex-1 px-2 py-1.5 border border-tint rounded text-xs bg-tint/40" data-testid="sign-link" />
              <Btn variant="ghost" onClick={() => copyLink(sigStatus.token)} testid="copy-link-btn"><Copy size={14} /> Copiar</Btn>
            </div>
            {sigStatus.estado === "assinada" && <div className="text-green-700 text-sm">Assinado em {dt(sigStatus.assinado_em)}.</div>}
          </div>
        ) : (
          <div className="space-y-3">
            <Field label="Nome do signatario" value={signer.signatario_nome} onChange={(v) => setSigner({ ...signer, signatario_nome: v })} testid="signer-nome" />
            <Field label="Email" value={signer.signatario_email} onChange={(v) => setSigner({ ...signer, signatario_email: v })} testid="signer-email" />
            <Field label="Telefone (E.164, ex.: +3519...)" value={signer.signatario_telefone} onChange={(v) => setSigner({ ...signer, signatario_telefone: v })} testid="signer-telefone" />
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={signer.email} onChange={(e) => setSigner({ ...signer, email: e.target.checked })} data-testid="canal-email" /> Email</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={signer.sms} onChange={(e) => setSigner({ ...signer, sms: e.target.checked })} data-testid="canal-sms" /> SMS</label>
            </div>
            <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShowSign(null)}>Cancelar</Btn><Btn onClick={sendSign} testid="send-signature-btn"><Send size={15} /> Enviar para assinatura</Btn></div>
          </div>
        )}
      </Modal>
      {drawer && (
        <ProposalDrawer
          proposal={drawer}
          open={!!drawer}
          onClose={() => setDrawer(null)}
          accounts={accounts}
          onOpenPdf={(p) => openPdf(p.id)}
          onOpenSign={(p) => { setDrawer(null); openSign(p); }}
          onAccepted={() => { setDrawer(null); load(); }}
        />
      )}
    </>
  );
}
